import { useState } from 'react';
import { useDispatch } from 'react-redux';
import {
    Button,
    DownloadTemplate,
    FieldErrorList,
    FileUploader,
    FormField,
    Icon,
    Input,
    Label,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    TextArea,
    Tooltip
} from 'common/components';
import RadioButton from 'common/components/base/radio-button/RadioButton';
import {
    FILE_COUNT_ERROR,
    FILE_COUNT_MAX,
    FILE_TYPE_ERROR,
    SUB_CATEGORY_DESCRIPTION_INVALID_CHAR_ERROR,
    SUB_CATEGORY_DESCRIPTION_MAX_LENGTH_ERROR,
    SUB_CATEGORY_NAME_EMPTY_ERROR,
    SUB_CATEGORY_NAME_INVALID_CHAR_ERROR,
    SUB_CATEGORY_NAME_MAX_LENGTH,
    SUB_CATEGORY_NAME_MAX_LENGTH_ERROR,
    SUB_CATEGORY_ORDER_EMPTY_ERROR,
    ADD_DISABLED_IN_REVIEW_TOOLTIP,
    SUB_CATEGORY_DESCRIPTION_MAX_LENGTH,
    SUB_CATEGORY_ORDER_GREATER_THAN_ZERO,
    SUB_CATEGORY_ORDER_INVALID_ERROR,
    SUB_CATEGORY_ORDER_MAX_LENGTH,
    SUB_CATEGORY_ORDER_MAX_LENGTH_ERROR,
    ACTION_ITEMS
} from 'common/config';
import { CategoryField, EntityType, InputType } from 'common/enums';
import { FieldError, FileDetails } from 'common/models';
import { addValidationError, removeValidationError } from 'common/utils/error';
import { masterDataActions } from 'modules/master-data/redux';
import { fileUploadActions } from 'common/redux/file-upload';
import { isAllowedFileType } from 'common/utils/file';
import { EntityVM, MasterDataEntity } from 'common/models/data/entity.model';
import { validateInputString, validateMaxLength, validateNumbers } from 'common/utils/validator';
import { FileUploadVariants } from 'common/enums/variants.enum';
import { ICON_COLLECTION } from 'assets/media/icons';

import SingleSelectMasterData from '../single-select-master-data/SingleSelectMasterData';

import './AddSubCategoryPopup.scss';
import { ADD_NEW_SUB_CATEGORY_BANNER_MESSAGE } from 'modules/configuration/config';

type AddSubCategoryPopupProps = {
    parentEntity: EntityVM;
    isVisible: boolean;
    isUploadDisabled: boolean;
    uploadedUser: string;
    fieldErrors: Array<FieldError>;
    uploadingFiles: Array<FileDetails>;
    selectedFiles: Array<FileDetails>;
    hierarchicalData: MasterDataEntity[];
    updateFieldErrors: (errors: Array<FieldError>) => void;
    updateSelectedRadioButton: (entity: EntityVM) => void;
    closePopupHandler: () => void;
    onUpload: () => void;
    toggleExpansion: (payload: EntityVM) => void;
    fetchNextHierachySet: () => void;
};
export default function AddSubCategoryPopup({
    isVisible,
    fieldErrors,
    isUploadDisabled,
    selectedFiles,
    uploadingFiles,
    hierarchicalData,
    uploadedUser,
    parentEntity,
    updateFieldErrors,
    updateSelectedRadioButton,
    closePopupHandler,
    onUpload,
    toggleExpansion,
    fetchNextHierachySet
}: AddSubCategoryPopupProps) {
    const [name, setName] = useState<string>('');
    const [sortOrder, setSortOrder] = useState<string>('');
    const [description, setDescription] = useState<string>('');
    const [uploadError, setUploadError] = useState<string>('');
    const [radioButtonSelected, setRadioButton] = useState('inputData');
    const [isFileUpload, setIsFileUpload] = useState(false);
    const [targetParentEntity, setTargetParentEntity] = useState<EntityVM>(parentEntity);
    const dispatch = useDispatch();

    const handleChange = (event: any) => {
        setRadioButton(event.target.value);
        if (event.target.value === 'uploadFile') {
            setIsFileUpload(true);
        } else {
            setIsFileUpload(false);
        }
    };

    const handleDescription = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        let errors = removeValidationError(fieldErrors, CategoryField.Description);
        if (validateMaxLength(event.target.value, SUB_CATEGORY_DESCRIPTION_MAX_LENGTH)) {
            errors = addValidationError(
                errors,
                CategoryField.Description,
                SUB_CATEGORY_DESCRIPTION_MAX_LENGTH_ERROR
            );
        }
        if (!validateInputString(event.target.value)) {
            errors = addValidationError(
                errors,
                CategoryField.Description,
                SUB_CATEGORY_DESCRIPTION_INVALID_CHAR_ERROR
            );
        }
        setDescription(event.target.value);
        updateFieldErrors(errors);
    };

    const handleNameChange = (event: any) => {
        let errors = removeValidationError(fieldErrors, CategoryField.Name);
        if (event.target.value.trim().length === 0) {
            errors = addValidationError(errors, CategoryField.Name, SUB_CATEGORY_NAME_EMPTY_ERROR);
        }
        if (validateMaxLength(event.target.value, SUB_CATEGORY_NAME_MAX_LENGTH)) {
            errors = addValidationError(
                errors,
                CategoryField.Name,
                SUB_CATEGORY_NAME_MAX_LENGTH_ERROR
            );
        }
        if (!validateInputString(event.target.value)) {
            errors = addValidationError(
                errors,
                CategoryField.Name,
                SUB_CATEGORY_NAME_INVALID_CHAR_ERROR
            );
        }
        setName(event.target.value);
        updateFieldErrors(errors);
    };

    const handleSortOrderChange = (value: string) => {
        let errors = removeValidationError(fieldErrors, CategoryField.SortOrder);
        if (value !== '') {
            if (!validateNumbers(value)) {
                errors = addValidationError(
                    errors,
                    CategoryField.SortOrder,
                    SUB_CATEGORY_ORDER_INVALID_ERROR
                );
            } else {
                if (Number(value) === 0) {
                    errors = addValidationError(
                        errors,
                        CategoryField.SortOrder,
                        SUB_CATEGORY_ORDER_GREATER_THAN_ZERO
                    );
                } else if (Number(value) > SUB_CATEGORY_ORDER_MAX_LENGTH) {
                    errors = addValidationError(
                        errors,
                        CategoryField.SortOrder,
                        SUB_CATEGORY_ORDER_MAX_LENGTH_ERROR
                    );
                }
            }
        } else {
            errors = addValidationError(
                errors,
                CategoryField.SortOrder,
                SUB_CATEGORY_ORDER_EMPTY_ERROR
            );
        }
        updateFieldErrors(errors);
        setSortOrder(value);
    };

    const saveSubCategoryHandler = () => {
        if (radioButtonSelected === 'inputData') {
            let errors = fieldErrors;
            if (!name.trim()) {
                errors = addValidationError(
                    errors,
                    CategoryField.Name,
                    SUB_CATEGORY_NAME_EMPTY_ERROR
                );
            }
            if (!sortOrder) {
                errors = addValidationError(
                    errors,
                    CategoryField.SortOrder,
                    SUB_CATEGORY_ORDER_EMPTY_ERROR
                );
            }
            if (errors.length === 0) {
                dispatch(
                    masterDataActions.addSubCategory({
                        categoryId:
                            targetParentEntity.entityType === EntityType.Category
                                ? targetParentEntity.id
                                : targetParentEntity.categoryId
                                ? targetParentEntity.categoryId
                                : '',
                        parentHierarchyId: targetParentEntity.hierarchyId,
                        parentSubCategoryId:
                            targetParentEntity.entityType === EntityType.Category
                                ? null
                                : targetParentEntity.id,
                        subCategoryName: name.trim(),
                        order: Number(sortOrder),
                        description: description,
                        subCategoryLevel: targetParentEntity.subCategoryLevel
                            ? targetParentEntity.subCategoryLevel + 1
                            : 1,
                        parentEntityType: targetParentEntity.entityType
                    })
                );
                closePopupHandler();
            }
            updateFieldErrors(errors);
        } else {
            dispatch(
                fileUploadActions.uploadFile({
                    files: selectedFiles,
                    variantName: FileUploadVariants.MasterData,
                    targetId:
                        targetParentEntity.entityType === EntityType.Category
                            ? targetParentEntity.id
                            : targetParentEntity.hierarchyId,
                    entityType: EntityType.SubCategory
                })
            );
            onUpload();
        }
    };

    const handleDrop = (files: Array<FileDetails>) => {
        let isAllowedType = true;

        setUploadError('');
        dispatch(fileUploadActions.setSelectedFiles([]));

        if (files.length > FILE_COUNT_MAX) {
            setUploadError(FILE_COUNT_ERROR);
        } else {
            const newFiles = files.map((item: FileDetails) => {
                item.targetID =
                    targetParentEntity.entityType === EntityType.Category
                        ? targetParentEntity.id
                        : targetParentEntity.hierarchyId
                        ? targetParentEntity.hierarchyId
                        : '';
                isAllowedType = isAllowedFileType(item, /(\.xls|\.xlsx)$/i);
                return item;
            });
            if (isAllowedType) {
                dispatch(fileUploadActions.setSelectedFiles(newFiles));
            } else {
                setUploadError(FILE_TYPE_ERROR);
            }
        }
    };

    const handleSelectedFileRemove = (file: FileDetails) => () => {
        const updatedFiles = selectedFiles
            ? selectedFiles.filter((item: FileDetails) => file.name !== item.name)
            : [];
        dispatch(fileUploadActions.setSelectedFiles(updatedFiles));
    };

    const handleClose = () => {
        setUploadError('');
        closePopupHandler();
    };

    const getSaveDisabledStatus = () => {
        if (radioButtonSelected === 'uploadFile') {
            return selectedFiles?.length < 1;
        } else {
            return fieldErrors.length > 0;
        }
    };

    /**
     * To update the parent entity on radio button select
     */
    const updateParentEntity = (value: EntityVM) => {
        setTargetParentEntity(value);
        updateSelectedRadioButton(value);
    };

    return (
        <Modal
            show={isVisible}
            onClose={closePopupHandler}
            style={{ width: '80vw', height: '95vh' }}>
            <ModalHeader>Add subcategory</ModalHeader>
            <ModalBody className="add-sub-category-popup-body">
                <div className="add-sub-category-container">
                    <div className="add-sub-category-container__left-Container">
                        <Label text={ADD_NEW_SUB_CATEGORY_BANNER_MESSAGE}></Label>
                        <SingleSelectMasterData
                            hideSelectionOnCategory={false}
                            hierarchicalData={hierarchicalData}
                            updateSelectedRadioButton={updateParentEntity}
                            toggleExpansion={toggleExpansion}
                            hideChildIndicatorsOnly={true}
                            preventSelectionOfSubCategoriesHavingIndicators={true}
                            fetchNextHierachySet={fetchNextHierachySet}
                        />
                    </div>
                    <div className="add-sub-category-container__right-Container">
                        <Label text="Fill in the below form"></Label>
                        <div className="add-sub-category-container__right-Container__input-type-container">
                            <RadioButton
                                name="radioButton"
                                id="inputData"
                                value="inputData"
                                checked={radioButtonSelected === 'inputData'}
                                onChange={(event) => handleChange(event)}>
                                Input Data
                            </RadioButton>
                            <RadioButton
                                name="radioButton"
                                id="uploadFile"
                                value="uploadFile"
                                className="radio-button-upload"
                                disabled={isUploadDisabled || !parentEntity.fromMaster}
                                checked={radioButtonSelected === 'uploadFile'}
                                onChange={(event) => handleChange(event)}>
                                Upload file
                            </RadioButton>
                            {(isUploadDisabled || !parentEntity.fromMaster) && (
                                <Tooltip
                                    trigger={
                                        <Icon
                                            src={ICON_COLLECTION.IC_Help_Outline}
                                            className="upload-disabled-help"
                                        />
                                    }
                                    placement="top"
                                    hideCloseButton
                                    variant="alt"
                                    hideArrow={true}
                                    allowClick={false}>
                                    {isUploadDisabled
                                        ? `Upload is in progress, locked by ${uploadedUser}`
                                        : ADD_DISABLED_IN_REVIEW_TOOLTIP}
                                </Tooltip>
                            )}
                        </div>
                        <div
                            className="subcategory-form"
                            style={{ display: isFileUpload ? 'none' : 'block' }}>
                            <FormField className="subcategory-form-field">
                                <Label text="Subcategory name"></Label>
                                <Input
                                    type={InputType.TEXT}
                                    value={name}
                                    onChange={handleNameChange}
                                    onBlur={handleNameChange}
                                />
                                <FieldErrorList field={CategoryField.Name} errors={fieldErrors} />
                            </FormField>
                            <FormField className="subcategory-form-field">
                                <Label text="Sort Order (required)"></Label>
                                <Input
                                    type={InputType.TEXT}
                                    value={sortOrder}
                                    onChange={(event) => handleSortOrderChange(event.target.value)}
                                    onBlur={(event) => handleSortOrderChange(event.target.value)}
                                />
                                <FieldErrorList
                                    field={CategoryField.SortOrder}
                                    errors={fieldErrors}
                                />
                            </FormField>
                            <FormField className="subcategory-form-field">
                                <Label text="Description"></Label>
                                <TextArea
                                    value={description}
                                    onChange={handleDescription}
                                    onBlur={handleDescription}
                                />
                                <FieldErrorList
                                    field={CategoryField.Description}
                                    errors={fieldErrors}
                                    data-testid="error"
                                />
                            </FormField>
                        </div>
                        <div
                            className="fileuploader"
                            style={{ display: isFileUpload ? 'block' : 'none' }}>
                            <DownloadTemplate fromMaster={true} />
                            <FileUploader
                                maxFiles={FILE_COUNT_MAX}
                                selectedFiles={selectedFiles}
                                uploadingFiles={uploadingFiles}
                                onSelectedFileRemove={handleSelectedFileRemove}
                                errorMessage={uploadError}
                                onDrop={handleDrop}
                            />
                        </div>
                    </div>
                </div>
            </ModalBody>
            <ModalFooter>
                <Button
                    size="medium"
                    type="button"
                    disabled={getSaveDisabledStatus()}
                    onClick={saveSubCategoryHandler}>
                    {ACTION_ITEMS.SAVE_BUTTON}
                </Button>
                <Button size="medium" variant="secondary" type="button" onClick={handleClose}>
                    {ACTION_ITEMS.CANCEL_BUTTON}
                </Button>
            </ModalFooter>
        </Modal>
    );
}
